import React, { useState, useEffect } from 'react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { GoogleAddressInput } from '@components/atoms/GoogleAddressInput';
import {
  Warehouse,
  WarehouseCreateInput,
  WarehouseUpdateInput,
} from '@models/warehouse.model';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocationOnIcon from '@mui/icons-material/LocationOn';

interface WarehouseFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: WarehouseCreateInput | WarehouseUpdateInput) => Promise<void>;
  warehouse?: Warehouse | null;
  isLoading?: boolean;
}

export const WarehouseFormModal: React.FC<WarehouseFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  warehouse,
  isLoading = false,
}) => {
  const isEditing = !!warehouse;

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [address, setAddress] = useState('');
  const [lat, setLat] = useState<number | undefined>(undefined);
  const [long, setLong] = useState<number | undefined>(undefined);
  const [isActive, setIsActive] = useState(true);

  // Reset form on open
  useEffect(() => {
    if (isOpen) {
      if (warehouse) {
        setName(warehouse.name || '');
        setDescription(warehouse.description || '');
        setAddress(warehouse.address || '');
        setLat(warehouse.lat ?? undefined);
        setLong(warehouse.long ?? undefined);
        setIsActive(warehouse.isActive ?? true);
      } else {
        setName('');
        setDescription('');
        setAddress('');
        setLat(undefined);
        setLong(undefined);
        setIsActive(true);
      }
    }
  }, [isOpen, warehouse]);

  const handleSubmit = async () => {
    if (!name.trim()) return;

    const baseData = {
      name: name.trim(),
      description: description.trim() || undefined,
      address: address.trim() || undefined,
      lat: lat ?? undefined,
      long: long ?? undefined,
      isActive,
    };

    if (isEditing && warehouse) {
      await onSubmit({ warehouseId: warehouse.warehouseId, ...baseData });
    } else {
      await onSubmit(baseData as WarehouseCreateInput);
    }
  };

  const handleCoordinatesChange = (newLat?: number | null, newLong?: number | null) => {
    setLat(newLat ?? undefined);
    setLong(newLong ?? undefined);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? 'Edit Warehouse' : 'Add Warehouse'}
      confirmText={isEditing ? 'Save Changes' : 'Add Warehouse'}
      handleConfirm={handleSubmit}
      isLoading={isLoading}
      disableConfirm={!name.trim()}
      size="medium"
    >
      <div className="flex flex-col gap-5">
        {/* Location Section */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <LocationOnIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
            Location
          </h3>
          <div className="flex flex-col gap-4">
            <FormGroup label="Warehouse Name *" htmlFor="warehouseName">
              <Input
                id="warehouseName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Main Warehouse"
                autoFocus
              />
            </FormGroup>

            <FormGroup label="Description" htmlFor="warehouseDescription">
              <textarea
                id="warehouseDescription"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this warehouse..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                rows={2}
              />
            </FormGroup>

            <FormGroup label="Address" htmlFor="warehouseAddress">
              <GoogleAddressInput
                value={address}
                onChange={setAddress}
                onChangeCoor={handleCoordinatesChange}
                placeholder="Search for an address..."
              />
            </FormGroup>
          </div>
        </div>

        {/* Settings Section */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <InfoOutlinedIcon className="text-indigo-600" sx={{ fontSize: 18 }} />
            Settings
          </h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="warehouseStatus"
                  checked={isActive}
                  onChange={() => setIsActive(true)}
                  className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="warehouseStatus"
                  checked={!isActive}
                  onChange={() => setIsActive(false)}
                  className="w-4 h-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Inactive</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default WarehouseFormModal;
